import { http } from "@/config/httpCommon"
import Vue from "vue"
import axios from "axios"
import { handleResponseException } from "@/helpers/Helpers"

export default {
  state: {
    access_token: "",
    factories_sharepoint_settings: {},
    share_point_db_settings_not_found: false,
    uploadProgress: {},
    uploadPreparation: {},
    multiFileUpload: {
      total: 0,
      completed: 0,
      inProgress: false,
    },
    cancelTokens: {},
    thumbnails: {},
  },
  mutations: {
    SET_ACCESS_TOKEN(state, payload) {
      state.access_token = payload
    },
    SET_FACTORIES_SHAREPOINT_SETTINGS(state, payload) {
      state.factories_sharepoint_settings = payload
    },
    UPDATE_UPLOAD_PROGRESS(state, { fileName, progress }) {
      Vue.set(state.uploadProgress, fileName, progress)
    },
    CLEAR_FILE_UPLOAD_PROGRESS(state, fileName) {
      Vue.delete(state.uploadProgress, fileName)
    },
    SET_MULTI_FILE_UPLOAD(state, { total }) {
      state.multiFileUpload.total = total
      state.multiFileUpload.completed = 0
      state.multiFileUpload.inProgress = true
    },
    INCREMENT_COMPLETED_FILES(state) {
      state.multiFileUpload.completed++
      if (state.multiFileUpload.completed >= state.multiFileUpload.total) {
        state.multiFileUpload.inProgress = false
      }
    },
    RESET_MULTI_FILE_UPLOAD(state) {
      state.multiFileUpload.total = 0
      state.multiFileUpload.completed = 0
      state.multiFileUpload.inProgress = false
    },
    SET_UPLOAD_PREPARATION(state, { fileName, preparing }) {
      Vue.set(state.uploadPreparation, fileName, preparing)
    },
    CLEAR_UPLOAD_PREPARATION(state, fileName) {
      Vue.delete(state.uploadPreparation, fileName)
    },
    SET_CANCEL_TOKEN(state, { fileName, cancelToken }) {
      Vue.set(state.cancelTokens, fileName, cancelToken)
    },
    REMOVE_CANCEL_TOKEN(state, fileName) {
      Vue.delete(state.cancelTokens, fileName)
    },
    SET_FILE_THUMBNAILS(state, { itemId, thumbnails }) {
      Vue.set(state.thumbnails, itemId, thumbnails)
    },
  },
  actions: {
    async getFactorySharepointInfo({ commit }, payload) {
      const factory_ids = payload.order_factories.map((factory) => factory.id)
      await http
        .post("get-sharepoint-factory-info", { factory_ids })
        .then((res) => {
          commit("SET_ACCESS_TOKEN", res.data.result.access_token)
          commit(
            "SET_FACTORIES_SHAREPOINT_SETTINGS",
            res.data.result.factories_sharepoint_settings[payload.active_factory.id],
          )
        })
        .catch((err) => {
          handleResponseException(err)
        })
    },

    async handleSharepointDriveItemAction({ state, dispatch }, payload) {
      if (state.factories_sharepoint_settings.site_name) {
        const api_meta_data = await dispatch("getSharepointDriveActionItemMetaData", payload)
        const drive_item_action_response = await dispatch("getSharepointApiData", api_meta_data)
        return drive_item_action_response;
      } else {
        state.share_point_db_settings_not_found = true
        throw new Error("SharePoint settings not found")
      }
    },

    getSharepointDriveActionItemMetaData({ state }, payload) {
      const drive_id = state.factories_sharepoint_settings.drive_id
      const target_item_action = payload.target_item_action
      const drive_target_item_id = payload.target_item_id
      const drive_destination_item_id = payload.destination_item_id
      const handle_conflict = payload.handle_conflict

      const base_url = `drives/${drive_id}/items/${drive_target_item_id}`
      const conflictParam = handle_conflict ? "?@microsoft.graph.conflictBehavior=rename" : ""

      switch (target_item_action) {
        case "copy":
          return {
            url: `${base_url}/copy?@microsoft.graph.conflictBehavior=rename`,
            api_payload: {
              parentReference: {
                id: drive_destination_item_id,
                driveId: drive_id,
              },
            },
            api_action: "post",
          }
        case "cut":
          return {
            url: `${base_url}${conflictParam}`,
            api_payload: {
              parentReference: {
                id: drive_destination_item_id,
                driveId: drive_id,
              },
            },
            api_action: "patch",
          }
        case "move":
          // Using the move operation (which is a PATCH to update the parentReference)
          return {
            url: `${base_url}${conflictParam}`,
            api_payload: {
              parentReference: {
                id: drive_destination_item_id,
                driveId: drive_id,
              },
            },
            api_action: "patch",
          }
        case "delete":
          return {
            url: `${base_url}`,
            api_action: "delete",
          }
        case "rename":
          return {
            url: `${base_url}`,
            api_payload: {
              name: payload.new_name,
            },
            api_action: "patch",
          }
        case "new_folder":
          return {
            url: `drives/${drive_id}/items/${drive_destination_item_id}/children`,
            api_payload: {
              name: payload.folder_name,
              folder: {},
            },
            api_action: "post",
            query_params: "@microsoft.graph.conflictBehavior=rename",
          }
      }
    },

    async getSharepointApiData({ state }, payload) {
      const api_payload = payload.api_payload
      const api_action = payload.api_action
      const access_token = state.access_token

      if (access_token) {
        const site_url = payload.url
        const site_full_url = `${process.env.VUE_APP_SHARPOINT_END_POINT}/${site_url}`

        try {
          const response = await axios({
            method: api_action,
            url: site_full_url,
            headers: {
              Authorization: `Bearer ${access_token}`,
              Accept: "application/json",
            },
            data: api_payload,
          })

          return response.data
        } catch (error) {
           handleResponseException(error)
        }
      } else {
        state.share_point_db_settings_not_found = true
        throw new Error("SharePoint access token not found")
      }
    },

    // New method specifically for moving items
    async moveSharepointItem({ state, dispatch }, { itemId, destinationFolderId, handleConflict = false }) {
      if (!state.factories_sharepoint_settings.site_name) {
        state.share_point_db_settings_not_found = true
        throw new Error("SharePoint settings not found")
      }

      const payload = {
        target_item_action: "move",
        target_item_id: itemId,
        destination_item_id: destinationFolderId,
        handle_conflict: handleConflict,
      }

      const api_meta_data = await dispatch("getSharepointDriveActionItemMetaData", payload)
      return await dispatch("getSharepointApiData", api_meta_data)
    },

    async uploadSharepointFile({ state, commit }, payload) {
      const drive_id = state.factories_sharepoint_settings.drive_id
      const access_token = state.access_token
      const drive_destination_item_id = payload.destination_item_id
      const file_name = payload.file_name

      const site_url = `drives/${drive_id}/items/${drive_destination_item_id}:/${file_name}:/content`
      const site_full_url = `${process.env.VUE_APP_SHARPOINT_END_POINT}/${site_url}`

      const cancelTokenSource = axios.CancelToken.source()
      commit("SET_CANCEL_TOKEN", { fileName: payload.fileName, cancelToken: cancelTokenSource })

      try {
        const response = await axios({
          method: "put",
          url: site_full_url,
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          data: payload.file,
          onUploadProgress: (progressEvent) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100
            commit("UPDATE_UPLOAD_PROGRESS", { fileName: payload.file_name, progress })
          },
          cancelToken: cancelTokenSource.token,

        })

        commit("UPDATE_UPLOAD_PROGRESS", { fileName: payload.file_name, progress: 100 })

        setTimeout(() => {
          commit("CLEAR_FILE_UPLOAD_PROGRESS", payload.file_name)
        }, 1500)

        commit("INCREMENT_COMPLETED_FILES")
        return response
      } catch (error) {
        commit("INCREMENT_COMPLETED_FILES")
        handleResponseException(error)

        setTimeout(() => {
          commit("CLEAR_FILE_UPLOAD_PROGRESS", payload.file_name)
        }, 3000)

        throw error
      }
    },

    async uploadLargeSharepointFile({ state, commit, dispatch }, payload) {
      const drive_id = state.factories_sharepoint_settings.drive_id
      const access_token = state.access_token
      const drive_destination_item_id = payload.destination_item_id
      const file_name = payload.file_name
      const file = payload.file

      commit("SET_UPLOAD_PREPARATION", { fileName: file_name, preparing: true })

      const createSessionUrl = `${process.env.VUE_APP_SHARPOINT_END_POINT}/drives/${drive_id}/items/${drive_destination_item_id}:/${file_name}:/createUploadSession`

      try {
        const sessionResponse = await axios({
          method: "post",
          url: createSessionUrl,
          headers: {
            Authorization: `Bearer ${access_token}`,
            "Content-Type": "application/json",
          },
          data: {
            "@microsoft.graph.conflictBehavior": "rename",
            name: file_name,
          },
        })

        const uploadUrl = sessionResponse.data.uploadUrl
        // Keep the preparation state active until the first chunk is uploaded
        return dispatch("uploadFileInChunks", { file, uploadUrl, fileName: file_name })
      } catch (error) {
        commit("CLEAR_UPLOAD_PREPARATION", file_name)
        handleResponseException(error)
        throw error
      }
    },

    async uploadFileInChunks({ commit, state }, { file, uploadUrl, fileName }) {
      const chunkSize = 5 * 1024 * 1024
      const fileSize = file.size
      let start = 0
      let end = Math.min(chunkSize, fileSize)
      const access_token = state.access_token

      // Create a new CancelToken source
      const cancelTokenSource = axios.CancelToken.source()
      commit("SET_CANCEL_TOKEN", { fileName, cancelToken: cancelTokenSource })

      while (start < fileSize) {
        const chunk = file.slice(start, end)
        const contentRange = `bytes ${start}-${end - 1}/${fileSize}`

        try {
          const response = await axios.put(uploadUrl, chunk, {
            headers: {
              "Content-Range": contentRange,
            },
            maxRedirects: 0,
            cancelToken: cancelTokenSource.token,
          })

          const locationHeader = response.headers["location"]
          if (locationHeader) {
            const statusResponse = await axios.get(locationHeader, {
              headers: {
                Authorization: `Bearer ${access_token}`,
              },
              cancelToken: cancelTokenSource.token,
            })

            if (statusResponse.data.nextExpectedRanges) {
              [start] = statusResponse.data.nextExpectedRanges
              start = Number.parseInt(start)
              end = Math.min(start + chunkSize, fileSize)
            } else {
              commit("UPDATE_UPLOAD_PROGRESS", { fileName, progress: 100 })
              commit("REMOVE_CANCEL_TOKEN", fileName)
              commit("CLEAR_UPLOAD_PREPARATION", fileName) // Ensure preparation state is cleared

              setTimeout(() => {
                commit("CLEAR_FILE_UPLOAD_PROGRESS", fileName)
              }, 1500)

              return statusResponse.data
            }
          } else {
            if (response.data.nextExpectedRanges) {
              [start] = response.data.nextExpectedRanges
              start = Number.parseInt(start)
              end = Math.min(start + chunkSize, fileSize)
            } else {
              commit("UPDATE_UPLOAD_PROGRESS", { fileName, progress: 100 })
              commit("REMOVE_CANCEL_TOKEN", fileName)
              commit("CLEAR_UPLOAD_PREPARATION", fileName) // Ensure preparation state is cleared

              setTimeout(() => {
                commit("CLEAR_FILE_UPLOAD_PROGRESS", fileName)
              }, 1500)

              return response.data
            }
          }

          const progress = Math.min(99, (start / fileSize) * 100)
          commit("UPDATE_UPLOAD_PROGRESS", { fileName, progress })

          // Clear the preparation state after the first chunk is uploaded
          if (start > 0) {
            commit("CLEAR_UPLOAD_PREPARATION", fileName)
          }
        } catch (error) {
          if (axios.isCancel(error)) {
            commit("REMOVE_CANCEL_TOKEN", fileName)
            commit("CLEAR_FILE_UPLOAD_PROGRESS", fileName)
            return { canceled: true }
          } else if (error.response && error.response.status === 302) {
            uploadUrl = error.response.headers["location"]
          } else {
            commit("REMOVE_CANCEL_TOKEN", fileName)
            handleResponseException(error)
            throw error
          }
        }
      }
      commit("UPDATE_UPLOAD_PROGRESS", { fileName, progress: 100 })
      commit("REMOVE_CANCEL_TOKEN", fileName)
      commit("CLEAR_UPLOAD_PREPARATION", fileName) // Ensure preparation state is cleared

      // Use a more reliable approach to clear the progress
      setTimeout(() => {
        commit("CLEAR_FILE_UPLOAD_PROGRESS", fileName)
      }, 1500)
    },

    cancelUpload({ state, commit }, fileName) {
      const cancelToken = state.cancelTokens[fileName]
      if (cancelToken) {
        cancelToken.cancel("Upload canceled by user")
        commit("REMOVE_CANCEL_TOKEN", fileName)
        commit("CLEAR_FILE_UPLOAD_PROGRESS", fileName)
      }
    },

    async getFileThumbnails({ state, commit }, payload) {
      const access_token = state.access_token
      const drive_id = state.factories_sharepoint_settings.drive_id

      // Check if we already have this thumbnail
      if (state.thumbnails[payload.id]) {
        return state.thumbnails[payload.id]
      }

      try {
        const url = `${process.env.VUE_APP_SHARPOINT_END_POINT}/drives/${drive_id}/items/${payload.id}/thumbnails`

        const response = await axios({
          method: "get",
          url: url,
          headers: {
            Authorization: `Bearer ${access_token}`,
            "Content-Type": "application/json",
          },
        })

        if (response.data && response.data.value && response.data.value.length > 0) {
          const thumbnailData = response.data.value[0]
          commit("SET_FILE_THUMBNAILS", {
            itemId: payload.id,
            thumbnails: thumbnailData,
          })
          return thumbnailData
        }
        return null
      } catch (error) {
        console.error("Error fetching thumbnails:", error)
        return null
      }
    },
  },
  getters: {
    getAccessToken: (state) => state.access_token,
    getFactoriesSharepointSettings: (state) => state.factories_sharepoint_settings,
    getMultiFileUploadStatus: (state) => state.multiFileUpload,
    getFileThumbnails: (state) => (itemId) => state.thumbnails[itemId] || null,
  },
}